import { Link } from 'react-router-dom'
import Layout from '@/components/Layout'
import SeoHead from '@/components/SeoHead'
import { PROTOCOL_CHAIN } from '@/lib/protocol'

function Disclaimer() {
  return (
    <Layout>
      <SeoHead
        title="Disclaimer"
        description={`Penguin Pad disclaimer: we are not responsible for your funds. Do your own research before launching, trading, or contributing on ${PROTOCOL_CHAIN}.`}
      />

      <article className="mx-auto max-w-3xl px-4 pb-20 pt-12 sm:px-6 lg:px-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Legal</p>
        <h1 className="mt-1 text-3xl font-bold text-white sm:text-4xl">Disclaimer</h1>
        <p className="mt-2 text-sm text-zinc-500">
          Last updated {new Date().getFullYear()}. Settlement on {PROTOCOL_CHAIN}.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-zinc-400">
          Penguin Pad is a permissionless interface for bonding-curve launches and community
          presales. Nothing on this site is financial, legal, tax, or investment advice. You use
          the protocol at your own risk. If you cannot afford to lose the entire amount, do not
          participate.
        </p>

        <div className="mt-8 rounded-3xl border border-orange-400/25 bg-orange-400/[0.06] p-5 sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-300">
            Not responsible for funds
          </p>
          <p className="mt-2 text-sm leading-relaxed text-zinc-300">
            Penguin Pad, its operators, and its contributors are not responsible for any ETH,
            tokens, or other value you send, receive, lock, lose, or fail to recover. Confirmed
            on-chain transactions cannot be reversed by this website. Do your own research.
          </p>
        </div>

        <Section title="1. Not financial advice">
          <p>
            Listings, charts, calculators, social links, and copy on Penguin Pad are for
            information only. They are not a recommendation to buy, sell, contribute, or launch.
            Token prices can go to zero. Presales can fail. You must independently evaluate every
            project, team, contract, and market.
          </p>
        </Section>

        <Section title="2. You can lose everything">
          <ul>
            <li>
              Experimental tokens and community raises are high risk. You may lose 100% of any
              ETH you spend on a launch, trade, contribution, or launch fee.
            </li>
            <li>
              Smart contracts can contain bugs, be exploited, or behave differently than the UI
              describes. Interface quotes, USD conversions, and progress bars can lag or be wrong.
              On-chain settlement is the source of truth.
            </li>
            <li>
              Permissionless listings are not vetted, audited, or endorsed by Penguin Pad. Anyone
              with a wallet can create a project. Scams, abandoned raises, and failed launches
              can and do appear.
            </li>
          </ul>
        </Section>

        <Section title="3. We do not hold your funds">
          <p>
            Penguin Pad never takes custody of your keys or your assets. You transact from a
            wallet you control on {PROTOCOL_CHAIN}. We cannot reverse a confirmed transaction,
            recover a wrong-network send, undo a phishing approval, or restore funds lost to
            user error, contract failure, market movement, or a project team.
          </p>
        </Section>

        <Section title="4. Do your own research">
          <p>
            Before you launch, buy, sell, or contribute, review the contracts, tokenomics, caps,
            timing, liquidity lock, and team identity yourself. Read the Terms. If something is
            unclear, do not transact. Your jurisdiction may restrict this activity — compliance
            is solely your responsibility.
          </p>
        </Section>

        <Section title="5. No warranties">
          <p>
            The interface is provided “as is,” without warranties of any kind, including
            uninterrupted uptime, accurate prices, successful raises, or recoverable liquidity.
            To the fullest extent allowed by law, Penguin Pad and its contributors are not liable
            for lost funds, lost tokens, lost profits, or any indirect or consequential damages
            arising from your use of this site or the protocol.
          </p>
        </Section>

        <div className="mt-10 flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-zinc-400">
            Trade and raise at your own risk. Read the full Terms before you connect a wallet.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/terms"
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-white hover:border-white/20"
            >
              Terms
            </Link>
            <Link
              to="/how-it-works"
              className="rounded-full bg-lime-400 px-4 py-2 text-sm font-semibold text-black hover:bg-lime-300"
            >
              How it works
            </Link>
          </div>
        </div>
      </article>
    </Layout>
  )
}

function Section({ title, children }: { title: string, children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-bold text-white">{title}</h2>
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-zinc-400 [&_li]:ml-4 [&_li]:list-disc [&_li]:pl-1 [&_ul]:space-y-2">
        {children}
      </div>
    </section>
  )
}

export default Disclaimer
